import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Link, Navigate } from "react-router-dom";
import Logo from "../components/Logo";
import {
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Store,
  CheckCircle2,
  XCircle,
  Mail,
  RefreshCw,
  Loader2,
  ChevronLeft,
  AlertTriangle,
  Clock,
  UserPlus,
  ShoppingBag,
  CreditCard,
  Activity,
  ExternalLink,
  Eye,
  RotateCcw,
  Zap,
  Bell,
  Info,
  ArrowRight,
  Power
} from "lucide-react";

const ADMIN_EMAILS = ["reynir@replydriven.com"];

const AdminOverview = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [retrying, setRetrying] = useState({});
  const [enablingBilling, setEnablingBilling] = useState(false);
  const [showBillingConfirm, setShowBillingConfirm] = useState(false);
  const [showOlderActivity, setShowOlderActivity] = useState(false);

  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const fetchData = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/admin/overview");
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch overview:", err);
      setError(err.response?.data?.detail || "Failed to load admin overview");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRetrySync = async (storeId) => {
    setRetrying(prev => ({ ...prev, [storeId]: true }));
    try {
      await api.post(`/admin/stores/${storeId}/retry-sync`);
      toast.success("Sync retry initiated");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to retry sync");
    } finally {
      setRetrying(prev => ({ ...prev, [storeId]: false }));
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return "—";
    return `${value}%`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "—";
    return value.toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getDeltaColor = (delta) => {
    if (delta > 0) return "text-green-400";
    if (delta < 0) return "text-red-400";
    return "text-text-muted";
  };

  const getDeltaIcon = (delta) => {
    if (delta > 0) return <TrendingUp className="w-3 h-3" />;
    if (delta < 0) return <TrendingDown className="w-3 h-3" />;
    return null;
  };

  const handleEnableBilling = async (sendEmails = false) => {
    setEnablingBilling(true);
    try {
      const res = await api.post("/admin/billing/enable", { send_emails: sendEmails });
      toast.success(res.data.message || "Billing enabled successfully");
      setShowBillingConfirm(false);
      fetchData(); // Refresh data
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to enable billing");
    } finally {
      setEnablingBilling(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "high": return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      case "medium": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case "signup": return <UserPlus className="w-4 h-4 text-blue-400" />;
      case "trial_started": return <Clock className="w-4 h-4 text-yellow-400" />;
      case "subscription_activated": return <CreditCard className="w-4 h-4 text-green-400" />;
      case "store_connected": return <Store className="w-4 h-4 text-purple-400" />;
      case "affiliate_approved": return <Users className="w-4 h-4 text-[#D4F34A]" />;
      case "report_sent": return <Mail className="w-4 h-4 text-blue-400" />;
      default: return <Activity className="w-4 h-4 text-text-muted" />;
    }
  };

  // Wait for auth to load before checking user/admin status
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/" />;
  if (!isAdmin) return <Navigate to="/dashboard" />;

  const kpis = data?.kpis || {};
  const needsAttention = data?.needs_attention || [];
  const rawActivity = data?.recent_activity || [];
  
  // Filter out test/example events per deterministic rules:
  // 1. Hide events where email contains @example.com
  // 2. Hide events where email starts with test_
  const filteredActivity = rawActivity.filter(event => {
    const summary = event.summary?.toLowerCase() || "";
    // Check for @example.com
    if (summary.includes("@example.com")) return false;
    // Check for test_ email patterns
    if (/test_[\w]+@/.test(summary)) return false;
    return true;
  });
  
  // Split into recent (7 days) and older
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  const recentActivity = filteredActivity.filter(event => {
    if (!event.at) return true;
    const eventDate = new Date(event.at);
    return eventDate >= sevenDaysAgo;
  });
  
  const olderActivity = filteredActivity.filter(event => {
    if (!event.at) return false;
    const eventDate = new Date(event.at);
    return eventDate < sevenDaysAgo;
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo showText linkTo="/" />
            <span className="text-text-muted">/</span>
            <Link to="/dashboard" className="text-text-muted hover:text-white text-sm">Dashboard</Link>
            <span className="text-text-muted">/</span>
            <span className="text-white text-sm font-medium">Admin</span>
          </div>
          
          <Link to="/dashboard">
            <Button variant="ghost" className="text-text-muted hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </Link>
        </div>
      </nav>

      {/* Admin Sub-Navigation */}
      <div className="fixed top-[65px] left-0 right-0 z-40 bg-[#0a0a0a] border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1">
            <Link
              to="/admin"
              className="px-4 py-3 text-sm font-medium border-b-2 border-[#D4F34A] text-white"
            >
              Overview
            </Link>
            <Link
              to="/admin/affiliates"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Affiliates
            </Link>
            <Link
              to="/admin/users"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Users
            </Link>
            <Link
              to="/admin/customers"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Customers
            </Link>
            <Link
              to="/admin/payouts"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Payouts
            </Link>
            <Link
              to="/admin/early-access"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
              data-testid="admin-nav-early-access"
            >
              Early Access
            </Link>
            <Link
              to="/admin/traffic"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
              data-testid="admin-nav-traffic"
            >
              Traffic
            </Link>
          </div>
        </div>
      </div>

      <main className="pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header with Status Badges */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-heading text-3xl font-bold">Admin Overview</h1>
                {/* Environment Badge */}
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  data?.environment === "production" 
                    ? "bg-green-500/20 text-green-400 border border-green-500/30" 
                    : "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                }`}>
                  {data?.environment === "production" ? "Production" : "Preview"}
                </span>
                {/* Billing Status Badge */}
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  data?.billing_enabled 
                    ? "bg-green-500/20 text-green-400 border border-green-500/30" 
                    : "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                }`}>
                  {data?.billing_enabled ? "Billing Enabled" : "Billing Pending"}
                </span>
              </div>
              <p className="text-text-muted text-sm">
                Last updated: {data?.generated_at ? formatDate(data.generated_at) : "—"}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Flip Billing Live Button - only show when billing is pending */}
              {data && !data.billing_enabled && (
                <Button
                  onClick={() => setShowBillingConfirm(true)}
                  className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold"
                >
                  <Power className="w-4 h-4 mr-2" />
                  Enable Billing
                </Button>
              )}
              <Button
                onClick={fetchData}
                disabled={loading}
                variant="outline"
                className="border-[#262626] text-white hover:bg-white/5"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </Button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <p className="text-red-400">{error}</p>
                </div>
                <Button onClick={fetchData} size="sm" className="bg-red-500 hover:bg-red-600 text-white">
                  Retry
                </Button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && !data && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[1,2,3,4].map(i => (
                  <div key={i} className="bg-surface border border-[#262626] rounded-lg p-5 animate-pulse">
                    <div className="h-4 bg-[#262626] rounded w-24 mb-3"></div>
                    <div className="h-8 bg-[#262626] rounded w-16"></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content */}
          {data && (
            <>
              {/* Billing Pending Warning Banner */}
              {!data.billing_enabled && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-yellow-400" />
                      <div>
                        <p className="text-yellow-400 font-medium">Billing is pending configuration</p>
                        <p className="text-sm text-text-muted">Revenue metrics are unavailable. Focus on funnel metrics below.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 7-Day Conversion Funnel (Primary when billing pending) */}
              <div className="mb-6">
                <h2 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#D4F34A]" />
                  7-Day Conversion Funnel
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Signups */}
                  <Card className="bg-surface border-[#262626]">
                    <CardContent className="py-5">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <UserPlus className="w-5 h-5 text-blue-400" />
                        </div>
                        <span className="text-sm text-text-muted">Signups</span>
                      </div>
                      <p className="text-3xl font-bold text-white">{formatNumber(data.funnel_7d?.signups)}</p>
                      <p className="text-xs text-text-muted mt-1">New users (7d)</p>
                    </CardContent>
                  </Card>

                  {/* Stores Connected */}
                  <Card className="bg-surface border-[#262626]">
                    <CardContent className="py-5">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
                          <Store className="w-5 h-5 text-purple-400" />
                        </div>
                        <span className="text-sm text-text-muted">Stores Connected</span>
                      </div>
                      <p className="text-3xl font-bold text-white">{formatNumber(data.funnel_7d?.stores_connected)}</p>
                      <p className="text-xs text-text-muted mt-1">
                        {data.funnel_7d?.signup_to_store_rate !== null ? (
                          <span className="text-purple-400">{data.funnel_7d.signup_to_store_rate}% of signups</span>
                        ) : "—"}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Preview Reached */}
                  <Card className="bg-surface border-[#262626]">
                    <CardContent className="py-5">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                          <Eye className="w-5 h-5 text-emerald-400" />
                        </div>
                        <span className="text-sm text-text-muted">Preview Reached</span>
                      </div>
                      <p className="text-3xl font-bold text-white">{formatNumber(data.funnel_7d?.preview_reached)}</p>
                      <p className="text-xs text-text-muted mt-1">
                        {data.funnel_7d?.store_to_preview_rate !== null ? (
                          <span className="text-emerald-400">{data.funnel_7d.store_to_preview_rate}% of stores</span>
                        ) : "—"}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Notify Clicks */}
                  <Card className="bg-surface border-[#262626]">
                    <CardContent className="py-5">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center">
                          <Bell className="w-5 h-5 text-[#D4F34A]" />
                        </div>
                        <span className="text-sm text-text-muted">Notify Clicks</span>
                      </div>
                      <p className="text-3xl font-bold text-white">{formatNumber(data.funnel_7d?.notify_clicks)}</p>
                      <p className="text-xs text-text-muted mt-1">
                        {data.funnel_7d?.preview_to_notify_rate !== null ? (
                          <span className="text-[#D4F34A]">{data.funnel_7d.preview_to_notify_rate}% of previews</span>
                        ) : "—"}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Key Conversion Rates - prominently displayed */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {/* Connect Rate */}
                  <Card className="bg-surface border-[#262626]">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
                            <ArrowRight className="w-5 h-5 text-purple-400" />
                          </div>
                          <div>
                            <p className="text-sm text-text-muted">Connect Rate</p>
                            <p className="text-xs text-text-muted">Signups → Store Connected</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${
                            data.funnel_7d?.signup_to_store_rate > 50 ? "text-green-400" : 
                            data.funnel_7d?.signup_to_store_rate > 20 ? "text-yellow-400" : "text-text-muted"
                          }`}>
                            {data.funnel_7d?.signup_to_store_rate !== null 
                              ? `${data.funnel_7d.signup_to_store_rate}%` 
                              : "—"}
                          </p>
                          <p className="text-xs text-text-muted">
                            {formatNumber(data.funnel_7d?.stores_connected)} / {formatNumber(data.funnel_7d?.signups)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Notify Rate */}
                  <Card className="bg-surface border-[#262626]">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center">
                            <ArrowRight className="w-5 h-5 text-[#D4F34A]" />
                          </div>
                          <div>
                            <p className="text-sm text-text-muted">Notify Rate</p>
                            <p className="text-xs text-text-muted">Preview → Notify Intent</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${
                            data.funnel_7d?.preview_to_notify_rate > 50 ? "text-green-400" : 
                            data.funnel_7d?.preview_to_notify_rate > 20 ? "text-yellow-400" : "text-text-muted"
                          }`}>
                            {data.funnel_7d?.preview_to_notify_rate !== null 
                              ? `${data.funnel_7d.preview_to_notify_rate}%` 
                              : "—"}
                          </p>
                          <p className="text-xs text-text-muted">
                            {formatNumber(data.funnel_7d?.notify_clicks)} / {formatNumber(data.funnel_7d?.preview_reached)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Billing Intent Card (when billing pending) */}
              {!data.billing_enabled && (
                <div className="mb-6">
                  <Card className="bg-surface border-[#D4F34A]/30">
                    <CardContent className="py-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-9 h-9 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center">
                              <Zap className="w-5 h-5 text-[#D4F34A]" />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-text-muted">Billing Intent Rate</span>
                              <div className="group relative">
                                <Info className="w-3.5 h-3.5 text-text-muted cursor-help" />
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1a1a1a] border border-[#262626] rounded-lg text-xs text-text-muted w-48 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                  Users who clicked &quot;Notify me when billing opens&quot; divided by users with connected stores.
                                </div>
                              </div>
                            </div>
                          </div>
                          {kpis.users_with_stores > 0 ? (
                            <>
                              <p className="text-3xl font-bold text-white">
                                {kpis.notify_on_billing_count} / {kpis.users_with_stores}
                                {kpis.billing_intent_rate_pct !== null && (
                                  <span className="text-lg text-[#D4F34A] ml-2">({kpis.billing_intent_rate_pct}%)</span>
                                )}
                              </p>
                              <p className="text-xs text-text-muted mt-1">
                                {kpis.notify_on_billing_count} users waiting · {kpis.total_users} total users
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="text-3xl font-bold text-text-muted">No data yet</p>
                              <p className="text-xs text-text-muted mt-1">Waiting for users to connect stores</p>
                            </>
                          )}
                        </div>
                        <Link to="/admin/waitlist">
                          <Button variant="outline" size="sm" className="border-[#D4F34A]/30 text-[#D4F34A] hover:bg-[#D4F34A]/10">
                            <Eye className="w-4 h-4 mr-1" /> View Waitlist
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* KPI Row 1: Money & Growth (dimmed when billing pending) */}
              <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 ${!data.billing_enabled ? "opacity-50" : ""}`}>
                {/* Active Subscribers */}
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="text-sm text-text-muted">Active Subscribers</span>
                    </div>
                    <p className="text-3xl font-bold text-white">
                      {!data.billing_enabled ? "N/A" : formatNumber(kpis.active_subscribers)}
                    </p>
                    {data.billing_enabled && kpis.active_subscribers_delta_7d !== 0 && (
                      <div className={`flex items-center gap-1 mt-1 text-sm ${getDeltaColor(kpis.active_subscribers_delta_7d)}`}>
                        {getDeltaIcon(kpis.active_subscribers_delta_7d)}
                        <span>{kpis.active_subscribers_delta_7d > 0 ? "+" : ""}{kpis.active_subscribers_delta_7d} (7d)</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* MRR */}
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <span className="text-sm text-text-muted">MRR</span>
                    </div>
                    <p className="text-3xl font-bold text-white">
                      {!data.billing_enabled ? "N/A" : formatCurrency(kpis.mrr_usd)}
                    </p>
                    {data.billing_enabled && kpis.mrr_delta_7d_usd !== 0 && (
                      <div className={`flex items-center gap-1 mt-1 text-sm ${getDeltaColor(kpis.mrr_delta_7d_usd)}`}>
                        {getDeltaIcon(kpis.mrr_delta_7d_usd)}
                        <span>{kpis.mrr_delta_7d_usd > 0 ? "+" : ""}{formatCurrency(kpis.mrr_delta_7d_usd)} (7d)</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Trial → Paid */}
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-purple-400" />
                      </div>
                      <span className="text-sm text-text-muted">Trials → Paid (30d)</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{formatPercent(kpis.trial_to_paid_30d_pct)}</p>
                    <p className="text-xs text-text-muted mt-1">
                      {kpis.paid_converted_30d || 0} of {kpis.trials_started_30d || 0} trials
                    </p>
                  </CardContent>
                </Card>

                {/* Churn */}
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center">
                        <TrendingDown className="w-5 h-5 text-red-400" />
                      </div>
                      <span className="text-sm text-text-muted">Churn (30d)</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{formatPercent(kpis.churn_30d_pct)}</p>
                    <p className="text-xs text-text-muted mt-1">
                      {kpis.cancellations_30d || 0} cancellations
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* KPI Row 2: Product Health */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                {/* Connected Stores */}
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center">
                        <Store className="w-5 h-5 text-[#D4F34A]" />
                      </div>
                      <span className="text-sm text-text-muted">Connected Stores</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{formatNumber(kpis.connected_stores_total)}</p>
                  </CardContent>
                </Card>

                {/* Stores OK */}
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      </div>
                      <span className="text-sm text-text-muted">Stores OK (24h)</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{formatNumber(kpis.stores_ok_24h)}</p>
                  </CardContent>
                </Card>

                {/* Stores Failing */}
                <Card className={`bg-surface border-[#262626] ${kpis.stores_fail_24h > 0 ? "border-red-500/30" : ""}`}>
                  <CardContent className="py-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${kpis.stores_fail_24h > 0 ? "bg-red-500/10" : "bg-gray-500/10"}`}>
                        <XCircle className={`w-5 h-5 ${kpis.stores_fail_24h > 0 ? "text-red-400" : "text-gray-400"}`} />
                      </div>
                      <span className="text-sm text-text-muted">Stores Failing</span>
                    </div>
                    <p className={`text-3xl font-bold ${kpis.stores_fail_24h > 0 ? "text-red-400" : "text-white"}`}>
                      {formatNumber(kpis.stores_fail_24h)}
                    </p>
                  </CardContent>
                </Card>

                {/* Reports Sent */}
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="text-sm text-text-muted">Reports Sent</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{formatNumber(kpis.reports_sent_last_run)}</p>
                    <p className="text-xs text-text-muted mt-1">
                      Last run: {kpis.last_report_run_at ? formatTimeAgo(kpis.last_report_run_at) : "Never"}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Needs Attention */}
              <div className="mb-8">
                <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  Needs Attention
                  {needsAttention.length > 0 && (
                    <span className="bg-yellow-500/20 text-yellow-400 text-xs px-2 py-0.5 rounded-full">
                      {needsAttention.length}
                    </span>
                  )}
                </h2>
                
                {needsAttention.length === 0 ? (
                  <Card className="bg-surface border-[#262626]">
                    <CardContent className="py-8 text-center">
                      <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
                      <p className="text-text-muted">All clear! Nothing needs your attention right now.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="bg-surface border border-[#262626] rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-[#1a1a1a]">
                        <tr>
                          <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">Type</th>
                          <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">Item</th>
                          <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">Reason</th>
                          <th className="text-left p-4 text-xs font-medium text-text-muted uppercase">Last Seen</th>
                          <th className="text-right p-4 text-xs font-medium text-text-muted uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#262626]">
                        {needsAttention.map(item => (
                          <tr key={item.id} className="hover:bg-white/5">
                            <td className="p-4">
                              <span className={`px-2 py-1 text-xs rounded-full border ${getSeverityColor(item.severity)}`}>
                                {item.type.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td className="p-4">
                              <p className="font-medium text-white text-sm">{item.title}</p>
                            </td>
                            <td className="p-4">
                              <p className="text-sm text-text-muted truncate max-w-xs">{item.detail}</p>
                            </td>
                            <td className="p-4 text-sm text-text-muted">
                              {formatTimeAgo(item.last_seen_at)}
                            </td>
                            <td className="p-4 text-right">
                              {item.type === "affiliate_pending" ? (
                                <Link to="/admin/affiliates">
                                  <Button size="sm" className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 text-xs">
                                    Review
                                  </Button>
                                </Link>
                              ) : item.type === "shopify_sync_failed" ? (
                                <Button
                                  size="sm"
                                  onClick={() => handleRetrySync(item.metadata?.store_id)}
                                  disabled={retrying[item.metadata?.store_id]}
                                  className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 text-xs"
                                >
                                  {retrying[item.metadata?.store_id] ? (
                                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                  ) : (
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                  )}
                                  Retry
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" className="text-text-muted text-xs">
                                  <Eye className="w-3 h-3 mr-1" /> View
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Recent Activity */}
              <div>
                <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-text-muted" />
                  Recent Activity
                  {filteredActivity.length > 0 && (
                    <span className="text-xs text-text-muted font-normal">
                      (last 7 days)
                    </span>
                  )}
                </h2>
                
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-2">
                    {recentActivity.length === 0 && olderActivity.length === 0 ? (
                      <p className="text-text-muted text-center py-6">No recent activity</p>
                    ) : (
                      <div>
                        {/* Recent events (last 7 days) */}
                        {recentActivity.length > 0 ? (
                          <div className="divide-y divide-[#262626]">
                            {recentActivity.map((event, idx) => (
                              <div key={idx} className="flex items-center gap-4 py-3">
                                <div className="flex-shrink-0">
                                  {getEventIcon(event.event_type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-white truncate">{event.summary}</p>
                                </div>
                                <div className="flex-shrink-0 text-xs text-text-muted">
                                  {formatTimeAgo(event.at)}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-text-muted text-center py-4 text-sm">No activity in the last 7 days</p>
                        )}
                        
                        {/* Older events - collapsible */}
                        {olderActivity.length > 0 && (
                          <div className="border-t border-[#262626] mt-2 pt-2">
                            <button
                              onClick={() => setShowOlderActivity(!showOlderActivity)}
                              className="w-full text-center py-2 text-sm text-text-muted hover:text-white transition-colors flex items-center justify-center gap-2"
                            >
                              <Clock className="w-3.5 h-3.5" />
                              {showOlderActivity ? "Hide" : "Show"} older activity ({olderActivity.length})
                            </button>
                            
                            {showOlderActivity && (
                              <div className="divide-y divide-[#262626] opacity-60">
                                {olderActivity.map((event, idx) => (
                                  <div key={`old-${idx}`} className="flex items-center gap-4 py-3">
                                    <div className="flex-shrink-0">
                                      {getEventIcon(event.event_type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-white truncate">{event.summary}</p>
                                    </div>
                                    <div className="flex-shrink-0 text-xs text-text-muted">
                                      {formatTimeAgo(event.at)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
      
      {/* Billing Confirmation Modal */}
      {showBillingConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#D4F34A]/10 flex items-center justify-center">
                <Power className="w-5 h-5 text-[#D4F34A]" />
              </div>
              <h3 className="text-xl font-bold text-white">Enable Billing</h3>
            </div>
            
            <p className="text-text-muted mb-4">
              You&apos;re about to flip billing live. This will allow users to start trials and subscribe.
            </p>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-400">
                <strong>Important:</strong> This change is in-memory only. Update{" "}
                <code className="bg-[#262626] px-1 rounded">BILLING_ENABLED=true</code>{" "}
                in <code className="bg-[#262626] px-1 rounded">.env</code> for persistence.
              </p>
            </div>
            
            <p className="text-white mb-4">
              Send &quot;billing is live&quot; email to all waitlist users now?
            </p>
            
            <div className="text-sm text-text-muted mb-6 flex items-center gap-2">
              <Bell className="w-4 h-4" />
              <span>{kpis.notify_on_billing_count || 0} users on the waitlist</span>
            </div>
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => handleEnableBilling(true)}
                disabled={enablingBilling}
                className="w-full bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold"
              >
                {enablingBilling ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Enable & Send Emails
              </Button>
              
              <Button
                onClick={() => handleEnableBilling(false)}
                disabled={enablingBilling}
                variant="outline"
                className="w-full border-[#262626] text-white hover:bg-white/5"
              >
                {enablingBilling ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Power className="w-4 h-4 mr-2" />
                )}
                Enable Without Emails
              </Button>
              
              <Button
                onClick={() => setShowBillingConfirm(false)}
                disabled={enablingBilling}
                variant="ghost"
                className="w-full text-text-muted hover:text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOverview;
